/**
 * tableColumns.js
 * ─────────────────────────────────────────────────────────────────────────
 * Centralized SmartTable column definitions for every table in the app.
 *
 * Each export is a *function* that receives live data / handlers so that
 * `filterConfig.options` are always derived from real records.
 *
 * Import pattern:
 *   import { getFortnightlyColumns } from "./tableColumns";
 * ─────────────────────────────────────────────────────────────────────────
 */

import React from "react";
import { Box, Text, Flex, Tag, Stack, Button } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { DeleteFilled, EditOutlined, CommentOutlined } from "../UI/Icons";
import { getAllTimes } from "../../Utils/auth";
import { UserRole } from "../../config/config";
import Reminder from "../Reminder";

// ─── Shared cell helpers ────────────────────────────────────────────────────

const StatusBadge = ({
  value,
  trueLabel = "Completed",
  falseLabel = "Pending",
}) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    px="8px"
    py="3px"
    borderRadius="full"
    bg={value ? "green.50" : "orange.50"}
    border="1px solid"
    borderColor={value ? "green.200" : "orange.200"}
    whiteSpace="nowrap"
    lineHeight="1"
  >
    <Text
      as="span"
      fontSize="11px"
      fontWeight="600"
      color={value ? "green.700" : "orange.600"}
      lineHeight="1"
    >
      {value ? trueLabel : falseLabel}
    </Text>
  </Box>
);

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

const extractClassName = (val) => {
  if (!val) return "—";
  if (typeof val === "object") {
    return val?.className || val?.name || "—";
  }
  return val;
};

export const UserRoleBadge = ({ user }) => {
  if (!user || typeof user !== "object") return null;
  const isDual =
    user?.isDualRole === true ||
    user?.isDualRole === "true" ||
    user?.access === "OT" ||
    user?.role === "OT" ||
    (Array.isArray(user?.roles) &&
      user.roles.includes("Observer") &&
      user.roles.includes("Teacher"));

  const badgeProps = {
    size: "sm",
    fontSize: "10px",
    fontWeight: "bold",
    px: 1.5,
    py: 0,
    h: "18px",
    minH: "18px",
    lineHeight: "18px",
    borderRadius: "md",
    flexShrink: 0,
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    m: 0,
    verticalAlign: "middle",
  };

  if (isDual) {
    return (
      <Tag
        {...badgeProps}
        colorScheme="teal"
        variant="solid"
        title="Observer & Teacher (Dual Role)"
      >
        OT
      </Tag>
    );
  }
  const access = user?.access || user?.role || user?.roleName;
  if (access === "Teacher") {
    return (
      <Tag
        {...badgeProps}
        colorScheme="green"
        variant="subtle"
        title="Teacher"
      >
        T
      </Tag>
    );
  }
  if (access === "Observer") {
    return (
      <Tag
        {...badgeProps}
        colorScheme="blue"
        variant="subtle"
        title="Observer"
      >
        O
      </Tag>
    );
  }
  return null;
};

export const UserCell = ({
  name,
  user,
  fontSize = "sm",
  fontWeight = "500",
  color,
}) => (
  <Flex
    align="center"
    gap={1.5}
    wrap="nowrap"
    m={0}
    p={0}
    display="inline-flex"
    verticalAlign="middle"
    lineHeight="1"
  >
    <Text
      as="span"
      fontSize={fontSize}
      fontWeight={fontWeight}
      color={color}
      whiteSpace="nowrap"
      m={0}
      p={0}
      lineHeight="1.2"
      display="inline-block"
      verticalAlign="middle"
    >
      {name || "—"}
    </Text>
    <UserRoleBadge user={user} />
  </Flex>
);

// ─────────────────────────────────────────────────────────────────────────────
// 1. FORTNIGHTLY MONITOR — Admin / Observer / Teacher list view
// ─────────────────────────────────────────────────────────────────────────────
export const getFortnightlyColumns = ({
  data = [],
  filterOptions = {},
  currentUserRole,
  onDelete,
}) => [
  {
    title: "Teacher",
    key: "teacherName",
    dataIndex: "teacherID",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.teacherID?.name || a?.userId?.name || "").localeCompare(
        b?.teacherID?.name || b?.userId?.name || "",
      ),
    render: (val, record) => {
      const userObj = val || record?.userId;
      return <UserCell name={userObj?.name} user={userObj} fontWeight="500" />;
    },
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.teacherID?.name || r?.userId?.name)),
      matchFn: (record, vals) =>
        vals.includes(record?.teacherID?.name || record?.userId?.name || ""),
    },
  },
  {
    title: "Observer",
    key: "observerName",
    dataIndex: "coordinatorID",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.coordinatorID?.name || a?.userId?.name || "").localeCompare(
        b?.coordinatorID?.name || b?.userId?.name || "",
      ),
    render: (val, record) => {
      const userObj = val || record?.userId;
      return <UserCell name={userObj?.name} user={userObj} color="gray.600" />;
    },
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.coordinatorID?.name || r?.userId?.name)),
      matchFn: (record, vals) =>
        vals.includes(
          record?.coordinatorID?.name || record?.userId?.name || "",
        ),
    },
  },
  {
    title: "Class",
    key: "className",
    dataIndex: "className",
    width: "110px",
    sortable: true,
    render: (val) => <Text fontSize="sm">{val || "—"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.classes?.length
        ? filterOptions.classes
        : uniq(data.map((r) => r.className)),
    },
  },
  {
    title: "Section",
    key: "section",
    dataIndex: "section",
    width: "90px",
    sortable: true,
    render: (val) => <Text fontSize="sm">{val || "—"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.sections?.length
        ? filterOptions.sections
        : uniq(data.map((r) => r.section)),
    },
  },
  {
    title: "Date",
    key: "date",
    dataIndex: "date",
    width: "120px",
    sortable: true,
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {val ? getAllTimes(val)?.formattedDate2 : "—"}
      </Text>
    ),
    filterConfig: {
      type: "date",
      matchFn: (record, val) =>
        !val ||
        new Date(record.date).toDateString() === new Date(val).toDateString(),
    },
  },
  {
    title: "Teacher Status",
    key: "isTeacherComplete",
    dataIndex: "isTeacherComplete",
    width: "140px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Observer Status",
    key: "isCoordinatorComplete",
    dataIndex: "isCoordinatorComplete",
    width: "150px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "320px",
    render: (_, record) => {
      const { isTeacherComplete, isCoordinatorComplete, isObserverInitiation } =
        record;
      const renderAction = () => {
        const canContinue =
          (currentUserRole === UserRole[2] && !isTeacherComplete && isObserverInitiation) ||
          (currentUserRole === UserRole[1] && isTeacherComplete && !isCoordinatorComplete);

        const canEdit =
          (currentUserRole === UserRole[2] && isTeacherComplete) ||
          (currentUserRole === UserRole[1] && isCoordinatorComplete) ||
          (isTeacherComplete && isCoordinatorComplete);

        const showReminder =
          (currentUserRole === UserRole[1] && !isTeacherComplete) ||
          (currentUserRole === UserRole[2] && isTeacherComplete && !isCoordinatorComplete);

        return (
          <Flex gap={2} align="center" flexWrap="nowrap">
            <Link to={`/fortnightly-monitor/report/${record._id}`}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                fontWeight="medium"
                flexShrink={0}
              >
                View Report
              </Button>
            </Link>

            {canContinue && (
              <Link to={`/fortnightly-monitor/create/${record._id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  Continue Form
                </Button>
              </Link>
            )}

            {canEdit && (
              <Link to={`/fortnightly-monitor/edit/${record._id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="green"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  Edit
                </Button>
              </Link>
            )}

            {showReminder && <Reminder id={record?._id} size="sm" />}
          </Flex>
        );
      };

      return (
        <Flex gap={2} align="center" flexWrap="nowrap">
          {renderAction()}
          {currentUserRole === UserRole[1] && onDelete && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              px={2.5}
              flexShrink={0}
              onClick={() => onDelete(record._id)}
              title="Delete"
            >
              <DeleteFilled />
            </Button>
          )}
        </Flex>
      );
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 2. CLASSROOM WALKTHROUGH columns
// ─────────────────────────────────────────────────────────────────────────────
export const getClassroomColumns = ({
  data = [],
  filterOptions = {},
  currentUserRole,
  onDelete,
}) => [
  {
    title: currentUserRole === UserRole[2] ? "Observer Name" : "Teacher Name",
    key: "personName",
    dataIndex: "grenralDetails",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) => {
      const aName =
        currentUserRole === UserRole[2]
          ? a?.createdBy?.name || ""
          : a?.grenralDetails?.NameoftheVisitingTeacher?.name || "";
      const bName =
        currentUserRole === UserRole[2]
          ? b?.createdBy?.name || ""
          : b?.grenralDetails?.NameoftheVisitingTeacher?.name || "";
      return aName.localeCompare(bName);
    },
    render: (val, record) => {
      const userObj =
        currentUserRole === UserRole[2]
          ? record?.createdBy
          : val?.NameoftheVisitingTeacher;
      return (
        <UserCell
          name={userObj?.name || (typeof userObj === "string" ? userObj : "—")}
          user={userObj}
          fontWeight="500"
        />
      );
    },
    filterConfig: {
      type: "select",
      options: uniq(
        data.map((r) =>
          currentUserRole === UserRole[2]
            ? r?.createdBy?.name
            : r?.grenralDetails?.NameoftheVisitingTeacher?.name,
        ),
      ),
      matchFn: (record, vals) => {
        const name =
          currentUserRole === UserRole[2]
            ? record?.createdBy?.name
            : record?.grenralDetails?.NameoftheVisitingTeacher?.name;
        return vals.includes(name || "");
      },
    },
  },
  {
    title: "Class",
    key: "className",
    dataIndex: "grenralDetails",
    width: "110px",
    sortable: true,
    sorter: (a, b) =>
      extractClassName(a?.grenralDetails?.className).localeCompare(
        extractClassName(b?.grenralDetails?.className),
      ),
    render: (val) => (
      <Text fontSize="sm">{extractClassName(val?.className)}</Text>
    ),
    filterConfig: {
      type: "select",
      options: filterOptions?.classes?.length
        ? filterOptions.classes
        : uniq(
            data.map((r) => extractClassName(r?.grenralDetails?.className)),
          ),
      matchFn: (record, vals) =>
        vals.includes(extractClassName(record?.grenralDetails?.className)),
    },
  },
  {
    title: "Section",
    key: "section",
    dataIndex: "grenralDetails",
    width: "90px",
    sortable: true,
    sorter: (a, b) =>
      (a?.grenralDetails?.Section || "").localeCompare(
        b?.grenralDetails?.Section || "",
      ),
    render: (val) => <Text fontSize="sm">{val?.Section || "—"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.sections?.length
        ? filterOptions.sections
        : uniq(data.map((r) => r?.grenralDetails?.Section)),
      matchFn: (record, vals) =>
        vals.includes(record?.grenralDetails?.Section || ""),
    },
  },
  {
    title: "Subject",
    key: "subject",
    dataIndex: "grenralDetails",
    width: "110px",
    sortable: true,
    sorter: (a, b) =>
      (a?.grenralDetails?.Subject || "").localeCompare(
        b?.grenralDetails?.Subject || "",
      ),
    render: (val) => <Text fontSize="sm">{val?.Subject || "—"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.subjects?.length
        ? filterOptions.subjects
        : uniq(data.map((r) => r?.grenralDetails?.Subject)),
      matchFn: (record, vals) =>
        vals.includes(record?.grenralDetails?.Subject || ""),
    },
  },
  {
    title: "Observation Date",
    key: "observationDate",
    dataIndex: "grenralDetails",
    width: "140px",
    sortable: true,
    sorter: (a, b) =>
      new Date(a?.grenralDetails?.DateOfObservation) -
      new Date(b?.grenralDetails?.DateOfObservation),
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {getAllTimes(val?.DateOfObservation)?.formattedDate2 || "—"}
      </Text>
    ),
    filterConfig: {
      type: "date",
      matchFn: (record, val) =>
        !val ||
        new Date(record?.grenralDetails?.DateOfObservation).toDateString() ===
          new Date(val).toDateString(),
    },
  },
  {
    title: "Teacher Status",
    key: "isTeacherCompletes",
    dataIndex: "isTeacherCompletes",
    width: "140px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Observer Status",
    key: "isObserverCompleted",
    dataIndex: "isObserverCompleted",
    width: "145px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "320px",
    render: (_, record) => {
      const { isTeacherCompletes, isObserverCompleted } = record;
      const renderAction = () => {
        return (
          <>
            <Link to={`/classroom-walkthrough/report/${record._id}`}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                fontWeight="medium"
                flexShrink={0}
              >
                View Report
              </Button>
            </Link>
            {currentUserRole === UserRole[1] && (
              <Link to={`/classroom-walkthrough/edit/${record._id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="green"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  Edit
                </Button>
              </Link>
            )}
            {currentUserRole === UserRole[2] && (!isTeacherCompletes || !isObserverCompleted) && (
              <Link to={`/classroom-walkthrough/create/${record._id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  Continue Form
                </Button>
              </Link>
            )}
            {currentUserRole === UserRole[1] && (!isTeacherCompletes || !isObserverCompleted) && (
              <Reminder id={record?._id} type="form2" size="sm" />
            )}
          </>
        );
      };

      return (
        <Flex gap={2} align="center" flexWrap="nowrap">
          {renderAction()}
          {currentUserRole === UserRole[1] && onDelete && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              px={2.5}
              flexShrink={0}
              onClick={() => onDelete(record._id)}
              title="Delete"
            >
              <DeleteFilled />
            </Button>
          )}
        </Flex>
      );
    },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 3. NOTEBOOK CHECKING columns
// ─────────────────────────────────────────────────────────────────────────────
export const getNotebookColumns = ({
  data = [],
  filterOptions = {},
  currentUserRole,
  onDelete,
  onComment,
  canComment = false,
}) => {
  const isSuperadmin =
    currentUserRole === "Superadmin" ||
    currentUserRole === "SuperAdmin" ||
    currentUserRole === UserRole[0];

  const cols = [];

  // 1. Observer Name (shown for Superadmin and Teacher views)
  if (isSuperadmin || currentUserRole === UserRole[2]) {
    cols.push({
      title: "Observer Name",
      key: "observerName",
      dataIndex: "grenralDetails",
      width: "230px",
      minWidth: "180px",
      sortable: true,
      sorter: (a, b) => {
        const aName = a?.grenralDetails?.NameofObserver?.name || "";
        const bName = b?.grenralDetails?.NameofObserver?.name || "";
        return aName.localeCompare(bName);
      },
      render: (val, record) => {
        const userObj = val?.NameofObserver;
        return (
          <UserCell
            name={userObj?.name || (typeof userObj === "string" ? userObj : "—")}
            user={userObj}
            fontWeight="500"
          />
        );
      },
      filterConfig: {
        type: "select",
        options: uniq(data.map((r) => r?.grenralDetails?.NameofObserver?.name)),
        matchFn: (record, vals) =>
          vals.includes(record?.grenralDetails?.NameofObserver?.name || ""),
      },
    });
  }

  // 2. Teacher Name (shown for Superadmin and Observer views)
  if (isSuperadmin || currentUserRole === UserRole[1]) {
    cols.push({
      title: "Teacher Name",
      key: "teacherName",
      dataIndex: "teacherID",
      width: "230px",
      minWidth: "180px",
      sortable: true,
      sorter: (a, b) => {
        const aName = a?.teacherID?.name || a?.createdBy?.name || "";
        const bName = b?.teacherID?.name || b?.createdBy?.name || "";
        return aName.localeCompare(bName);
      },
      render: (val, record) => {
        const userObj = record?.teacherID || record?.createdBy;
        return (
          <UserCell
            name={userObj?.name || (typeof userObj === "string" ? userObj : "—")}
            user={userObj}
            fontWeight="500"
          />
        );
      },
      filterConfig: {
        type: "select",
        options: uniq(data.map((r) => r?.teacherID?.name || r?.createdBy?.name)),
        matchFn: (record, vals) => {
          const name = record?.teacherID?.name || record?.createdBy?.name;
          return vals.includes(name || "");
        },
      },
    });
  }

  cols.push(
    {
      title: "Class",
      key: "className",
      dataIndex: "grenralDetails",
      width: "110px",
      sortable: true,
      sorter: (a, b) =>
        extractClassName(a?.grenralDetails?.className).localeCompare(
          extractClassName(b?.grenralDetails?.className),
        ),
      render: (val) => (
        <Text fontSize="sm">{extractClassName(val?.className)}</Text>
      ),
      filterConfig: {
        type: "select",
        options: filterOptions?.classes?.length
          ? filterOptions.classes
          : uniq(
              data.map((r) => extractClassName(r?.grenralDetails?.className)),
            ),
        matchFn: (record, vals) =>
          vals.includes(extractClassName(record?.grenralDetails?.className)),
      },
    },
    {
      title: "Section",
      key: "section",
      dataIndex: "grenralDetails",
      width: "90px",
      sortable: true,
      sorter: (a, b) =>
        (a?.grenralDetails?.Section || "").localeCompare(
          b?.grenralDetails?.Section || "",
        ),
      render: (val) => <Text fontSize="sm">{val?.Section || "—"}</Text>,
      filterConfig: {
        type: "select",
        options: filterOptions?.sections?.length
          ? filterOptions.sections
          : uniq(data.map((r) => r?.grenralDetails?.Section)),
        matchFn: (record, vals) =>
          vals.includes(record?.grenralDetails?.Section || ""),
      },
    },
    {
      title: "Subject",
      key: "subject",
      dataIndex: "grenralDetails",
      width: "120px",
      sortable: true,
      sorter: (a, b) =>
        (a?.grenralDetails?.Subject || "").localeCompare(
          b?.grenralDetails?.Subject || "",
        ),
      render: (val) => <Text fontSize="sm">{val?.Subject || "—"}</Text>,
      filterConfig: {
        type: "select",
        options: filterOptions?.subjects?.length
          ? filterOptions.subjects
          : uniq(data.map((r) => r?.grenralDetails?.Subject)),
        matchFn: (record, vals) =>
          vals.includes(record?.grenralDetails?.Subject || ""),
      },
    },
    {
      title: "Observation Date",
      key: "observationDate",
      dataIndex: "grenralDetails",
      width: "140px",
      sortable: true,
      sorter: (a, b) =>
        new Date(a?.grenralDetails?.DateOfObservation) -
        new Date(b?.grenralDetails?.DateOfObservation),
      render: (val) => (
        <Text fontSize="sm" color="gray.600">
          {getAllTimes(val?.DateOfObservation)?.formattedDate2 || "—"}
        </Text>
      ),
      filterConfig: {
        type: "date",
        matchFn: (record, val) =>
          !val ||
          new Date(record?.grenralDetails?.DateOfObservation).toDateString() ===
            new Date(val).toDateString(),
      },
    }
  );

  return [
    ...cols,

  {
    title: "Teacher Status",
    key: "isTeacherComplete",
    dataIndex: "isTeacherComplete",
    width: "145px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Observer Status",
    key: "isObserverComplete",
    dataIndex: "isObserverComplete",
    width: "150px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Reflection Status",
    key: "isReflation",
    dataIndex: "isReflation",
    width: "155px",
    render: (val) => <StatusBadge value={val} trueLabel="Submitted" falseLabel="Pending" />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Submitted",
      falseLabel: "Pending",
    },
  },

  {
    title: "Teacher Score",
    key: "teacherScore",
    width: "140px",
    render: (_, record) => {
      const score = calculateNotebookScore(record?.TeacherForm);
      return score.outOfScore > 0 ? (
        <Text fontSize="sm" fontWeight="600" color="brand.primary">
          {score.totalScore} / {score.outOfScore}
        </Text>
      ) : (
        <Text fontSize="sm" color="gray.400">
          —
        </Text>
      );
    },
  },
  {
    title: "Observer Score",
    key: "observerScore",
    width: "145px",
    render: (_, record) => {
      const score = calculateNotebookScore(record?.ObserverForm);
      return score.outOfScore > 0 ? (
        <Text fontSize="sm" fontWeight="600" color="brand.primary">
          {score.totalScore} / {score.outOfScore}
        </Text>
      ) : (
        <Text fontSize="sm" color="gray.400">
          —
        </Text>
      );
    },
  },

  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "320px",
    render: (_, record) => {
      const { isObserverComplete, isTeacherComplete, isReflation } = record;

      const renderAction = () => {
        // ── Step 3 zone: Observer has completed their form ──────────────────
        if (isObserverComplete) {
          // Fully done (Teacher reflection submitted) → everyone sees View Report
          if (isReflation) {
            return (
              <Flex gap={2} align="center" flexWrap="nowrap">
                <Link to={`/notebook-checking-proforma/report/${record._id}`}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                    fontWeight="medium"
                    flexShrink={0}
                  >
                    View Report
                  </Button>
                </Link>
              </Flex>
            );
          }

          // Teacher still needs to submit reflection (Step 3)
          if (currentUserRole === UserRole[2]) {
            return (
              <Link to={`/notebook-checking-proforma/complete/${record._id}`}>
                <Button
                  size="sm"
                  variant="solid"
                  colorScheme="orange"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  Give Reflection
                </Button>
              </Link>
            );
          }

          // Observer already done — show View Report
          return (
            <Flex gap={2} align="center" flexWrap="nowrap">
              <Link to={`/notebook-checking-proforma/report/${record._id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  View Report
                </Button>
              </Link>
            </Flex>
          );
        }

        // ── Step 2 zone: Teacher done, waiting for Observer ─────────────────
        if (isTeacherComplete) {
          if (currentUserRole === UserRole[1]) {
            return (
              <Link to={`/notebook-checking-proforma/create/${record._id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  Continue Form
                </Button>
              </Link>
            );
          } else {
            return <Reminder id={record?._id} type="form3" size="sm" />;
          }
        }

        // ── Step 1 zone: Teacher hasn't filled yet ──────────────────────────
        if (currentUserRole === UserRole[2]) {
          return (
            <Link to={`/notebook-checking-proforma/edit/${record._id}`}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                fontWeight="medium"
                flexShrink={0}
              >
                Continue Form
              </Button>
            </Link>
          );
        }

        return <Reminder id={record?._id} type="form3" size="sm" />;
      };

      const hasComment = Boolean(record?.observerComment || record?.observerFeedback);
      const recordObserver = record?.grenralDetails?.NameofObserver;
      const recordCreator = record?.createdBy;
      const isRecordObserverAllowed = Boolean(
        recordObserver?.canAddNotebookComment === true ||
        recordObserver?.canAddNotebookComment === "true" ||
        recordCreator?.canAddNotebookComment === true ||
        recordCreator?.canAddNotebookComment === "true"
      );
      const isAllowedToComment = Boolean(
        canComment ||
        isRecordObserverAllowed ||
        (typeof canComment === "function" && canComment(record))
      );

      return (
        <Flex gap={2} align="center" flexWrap="nowrap">
          {renderAction()}
          {isAllowedToComment && onComment && (
            <Button
              size="sm"
              variant={hasComment ? "solid" : "outline"}
              colorScheme="purple"
              fontWeight="medium"
              flexShrink={0}
              onClick={() => onComment(record)}
              title={hasComment ? "Edit Observer Comment" : "Add Observer Comment"}
              leftIcon={<CommentOutlined />}
            >
              {hasComment ? "Edit Comment" : "Comment"}
            </Button>
          )}
          {currentUserRole === UserRole[1] && onDelete && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              px={2.5}
              flexShrink={0}
              onClick={() => onDelete(record._id)}
              title="Delete"
            >
              <DeleteFilled />
            </Button>
          )}
        </Flex>
      );
    },
  },
];
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. WEEKLY 4 FORM columns
// ─────────────────────────────────────────────────────────────────────────────
export const getWeeklyColumns = ({ data = [], currentUserRole, onDelete }) => [
  {
    title: "Teacher Name",
    key: "teacherName",
    dataIndex: "teacherId",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.teacherId?.name || "").localeCompare(b?.teacherId?.name || ""),
    render: (val) => <UserCell name={val?.name || "N/A"} user={val} fontWeight="500" />,
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.teacherId?.name)),
      matchFn: (record, vals) => vals.includes(record?.teacherId?.name || ""),
    },
  },
  {
    title: "Observer",
    key: "observerName",
    dataIndex: "isInitiated",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.isInitiated?.Observer?.name || "").localeCompare(
        b?.isInitiated?.Observer?.name || "",
      ),
    render: (val) => (
      <UserCell
        name={val?.Observer?.name || "N/A"}
        user={val?.Observer}
        color="gray.600"
      />
    ),
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.isInitiated?.Observer?.name)),
      matchFn: (record, vals) =>
        vals.includes(record?.isInitiated?.Observer?.name || ""),
    },
  },
  {
    title: "Submission Date",
    key: "dateOfSubmission",
    dataIndex: "dateOfSubmission",
    width: "140px",
    sortable: true,
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {val ? getAllTimes(val)?.formattedDate2 : "—"}
      </Text>
    ),
    filterConfig: {
      type: "date",
      matchFn: (record, val) =>
        !val ||
        new Date(record.dateOfSubmission).toDateString() ===
          new Date(val).toDateString(),
    },
  },
  {
    title: "Status",
    key: "isCompleted",
    dataIndex: "isCompleted",
    width: "130px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "320px",
    render: (_, record) => (
      <Flex gap={2} align="center" flexWrap="nowrap">
        <Link to={`/weekly4form/report/${record._id}`}>
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            fontWeight="medium"
            flexShrink={0}
          >
            View Report
          </Button>
        </Link>
        {!record?.isCompleted && currentUserRole === UserRole[2] && (
          <Link to={`/weekly4form/create/${record?._id}`}>
            <Button
              size="sm"
              variant="outline"
              colorScheme="orange"
              fontWeight="medium"
              flexShrink={0}
            >
              Continue Form
            </Button>
          </Link>
        )}
        {currentUserRole === UserRole[1] && !record?.isCompleted && (
          <Reminder id={record?._id} type="form4" size="sm" />
        )}
        {(currentUserRole === UserRole[0] || currentUserRole === UserRole[1]) && onDelete && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            px={2.5}
            flexShrink={0}
            onClick={() => onDelete(record._id)}
            title="Delete"
          >
            <DeleteFilled />
          </Button>
        )}
      </Flex>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 5. USERS TABLE (Admin)
// ─────────────────────────────────────────────────────────────────────────────
export const getUserColumns = ({ onEdit, onDelete }) => [
  {
    title: "Employee ID",
    key: "employeeId",
    dataIndex: "employeeId",
    width: "130px",
    sortable: true,
    filterConfig: { type: "text" },
  },
  {
    title: "Name",
    key: "name",
    dataIndex: "name",
    width: "220px",
    minWidth: "200px",
    sortable: true,
    filterConfig: { type: "text" },
  },
  {
    title: "Access",
    key: "access",
    dataIndex: "access",
    width: "140px",
    render: (val, record) => {
      const isDual =
        record?.isDualRole === true ||
        record?.isDualRole === "true" ||
        (Array.isArray(record?.roles) &&
          record.roles.includes("Observer") &&
          record.roles.includes("Teacher"));
      if (val === "Superadmin") {
        return (
          <Tag colorScheme="purple" variant="subtle" fontWeight="bold">
            Superadmin
          </Tag>
        );
      }
      if (isDual) {
        return (
          <Tag
            colorScheme="teal"
            variant="solid"
            fontWeight="bold"
            title="Observer & Teacher (Dual Role)"
          >
            OT
          </Tag>
        );
      }
      if (val === "Teacher") {
        return (
          <Tag
            colorScheme="green"
            variant="subtle"
            fontWeight="bold"
            title="Teacher"
          >
            T
          </Tag>
        );
      }
      return (
        <Tag
          colorScheme="blue"
          variant="subtle"
          fontWeight="bold"
          title="Observer"
        >
          O
        </Tag>
      );
    },
    filterConfig: {
      type: "select",
      options: ["Superadmin", "Observer", "Teacher", "OT"],
      matchFn: (record, vals) => {
        const isDual =
          record?.isDualRole === true ||
          record?.isDualRole === "true" ||
          (Array.isArray(record?.roles) &&
            record.roles.includes("Observer") &&
            record.roles.includes("Teacher"));
        const tag = isDual ? "OT" : record?.access === "Teacher" ? "T" : record?.access === "Observer" ? "O" : record?.access;
        return (
          vals.includes(tag) ||
          vals.includes(record?.access) ||
          (isDual && vals.includes("OT"))
        );
      },
    },
  },
  {
    title: "Status",
    key: "isActive",
    dataIndex: "isActive",
    width: "120px",
    sortable: true,
    render: (val) => {
      const active = val !== false;
      return (
        <Tag
          colorScheme={active ? "green" : "red"}
          variant="subtle"
          fontWeight="bold"
        >
          {active ? "Active" : "Inactive"}
        </Tag>
      );
    },
    filterConfig: {
      type: "select",
      options: ["Active", "Inactive"],
      matchFn: (record, vals) => {
        const status = record?.isActive !== false ? "Active" : "Inactive";
        return vals.includes(status);
      },
    },
  },
  {
    title: "Custom ID",
    key: "customId",
    dataIndex: "customId",
    width: "130px",
    filterConfig: { type: "text" },
  },
  {
    title: "Created At",
    key: "createdAt",
    dataIndex: "createdAt",
    width: "130px",
    sortable: true,
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {getAllTimes(val)?.formattedDate2 || "—"}
      </Text>
    ),
    filterConfig: { type: "date" },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "240px",
    render: (_, record) => (
      <Stack direction="row" spacing={2} align="center">
        {onEdit && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="blue"
            color="blue.600"
            _hover={{ bg: "blue.50" }}
            leftIcon={<EditOutlined />}
            onClick={() => onEdit(record)}
          >
            Edit
          </Button>
        )}
        <Link to={`${record?._id}`}>
          <Button
            size="sm"
            variant="ghost"
            color="brand.primary"
            _hover={{ bg: "brand.background" }}
          >
            View
          </Button>
        </Link>
        <Button
          size="sm"
          variant="ghost"
          colorScheme="red"
          color="red.600"
          _hover={{ bg: "red.50" }}
          leftIcon={<DeleteFilled />}
          onClick={() => onDelete(record?._id)}
        >
          Delete
        </Button>
      </Stack>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 6. CLASS & SECTION TABLE (Admin)
// ─────────────────────────────────────────────────────────────────────────────
export const getClassSectionColumns = ({ onEdit, onDelete }) => [
  {
    title: "Class Name",
    key: "className",
    dataIndex: "className",
    width: "150px",
    sortable: true,
    render: (val) => (
      <Text fontWeight="600" fontSize="sm">
        {val || "—"}
      </Text>
    ),
    filterConfig: { type: "text" },
  },
  {
    title: "Sections",
    key: "sections",
    dataIndex: "sections",
    render: (sections) => (
      <Flex wrap="wrap" gap={1}>
        {(sections || []).map((s, i) => (
          <Tag key={i} variant="subtle" colorScheme="blue" fontSize="xs">
            {s.name || s}
          </Tag>
        ))}
      </Flex>
    ),
  },
  {
    title: "Subjects",
    key: "subjects",
    dataIndex: "subjects",
    render: (subjects) => (
      <Flex wrap="wrap" gap={1}>
        {(subjects || []).map((s, i) => (
          <Tag key={i} variant="subtle" colorScheme="green" fontSize="xs">
            {s.name || s}
          </Tag>
        ))}
      </Flex>
    ),
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "180px",
    render: (_, record) => (
      <Flex gap={2} align="center">
        {onEdit && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            leftIcon={<EditOutlined />}
            onClick={() => onEdit(record)}
          >
            Edit
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          colorScheme="red"
          color="red.600"
          _hover={{ bg: "red.50" }}
          leftIcon={<DeleteFilled />}
          onClick={() => onDelete(record)}
        >
          Delete
        </Button>
      </Flex>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 7. WING COORDINATOR TABLE
// ─────────────────────────────────────────────────────────────────────────────
export const getWingCoordinatorColumns = ({ data = [], onDuplicate }) => [
  {
    title: "Teacher",
    key: "teacherName",
    dataIndex: "teacherID",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.teacherID?.name || "").localeCompare(b?.teacherID?.name || ""),
    render: (val) => <UserCell name={val?.name || "—"} user={val} fontWeight="500" />,
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.teacherID?.name)),
      matchFn: (record, vals) => vals.includes(record?.teacherID?.name || ""),
    },
  },
  {
    title: "Date",
    key: "date",
    dataIndex: "date",
    width: "130px",
    sortable: true,
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {val ? getAllTimes(val)?.formattedDate2 : "—"}
      </Text>
    ),
    filterConfig: { type: "date" },
  },
  {
    title: "Status",
    key: "isCompleted",
    dataIndex: "isCompleted",
    width: "190px",
    render: (val, record) => {
      const isDuplicate = record?.isDuplicate || record?.formName?.includes("(Copy)");
      return (
        <Flex gap={2} align="center">
          <StatusBadge value={val} />
          {isDuplicate && (
            <Tag
              size="sm"
              colorScheme="purple"
              variant="subtle"
              borderRadius="full"
              fontWeight="600"
              px="8px"
              py="2px"
              fontSize="11px"
            >
              Duplicate
            </Tag>
          )}
        </Flex>
      );
    },
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "240px",
    render: (_, record) => (
      <Flex gap={2} align="center">
        {record?.isCompleted ? (
          <Link to={`/wing-coordinator/report/${record._id}`}>
            <Button
              size="md"
              variant="outline"
              colorScheme="blue"
              fontWeight="medium"
              flexShrink={0}
            >
              View Report
            </Button>
          </Link>
        ) : (
          <Link to={`/wing-coordinator/${record._id}`}>
            <Button
              size="md"
              variant="outline"
              colorScheme="blue"
              fontWeight="medium"
              flexShrink={0}
            >
              Continue
            </Button>
          </Link>
        )}
        {onDuplicate && (
          <Button
            size="md"
            variant="outline"
            colorScheme="purple"
            fontWeight="medium"
            flexShrink={0}
            onClick={() => onDuplicate(record._id)}
          >
            Duplicate
          </Button>
        )}
      </Flex>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 8. REPORTS — Form 1 (Fortnightly Monitor Report)
// ─────────────────────────────────────────────────────────────────────────────
export const getReportForm1Columns = ({
  data = [],
  filterOptions = {},
  currentUserRole,
  onDelete,
}) => [
  {
    title: "Observer Name",
    key: "observerName",
    dataIndex: "coordinatorID",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.coordinatorID?.name || a?.userId?.name || "").localeCompare(
        b?.coordinatorID?.name || b?.userId?.name || "",
      ),
    render: (val, record) => {
      const userObj = val || record?.userId;
      return <UserCell name={userObj?.name || "N/A"} user={userObj} fontWeight="500" />;
    },
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.coordinatorID?.name || r?.userId?.name)),
      matchFn: (record, vals) =>
        vals.includes(
          record?.coordinatorID?.name || record?.userId?.name || "",
        ),
    },
  },
  {
    title: "Teacher Name",
    key: "teacherName",
    dataIndex: "teacherID",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.teacherID?.name || a?.userId?.name || "").localeCompare(
        b?.teacherID?.name || b?.userId?.name || "",
      ),
    render: (val, record) => {
      const userObj = val || record?.userId;
      return <UserCell name={userObj?.name || "N/A"} user={userObj} />;
    },
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.teacherID?.name || r?.userId?.name)),
      matchFn: (record, vals) =>
        vals.includes(record?.teacherID?.name || record?.userId?.name || ""),
    },
  },
  {
    title: "Class",
    key: "className",
    dataIndex: "className",
    width: "120px",
    sortable: true,
    render: (val) => <Text fontSize="sm">{val || "N/A"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.classes?.length
        ? filterOptions.classes
        : uniq(data.map((r) => r.className)),
    },
  },
  {
    title: "Section",
    key: "section",
    dataIndex: "section",
    width: "90px",
    sortable: true,
    render: (val) => <Text fontSize="sm">{val || "N/A"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.sections?.length
        ? filterOptions.sections
        : uniq(data.map((r) => r.section)),
    },
  },
  {
    title: "Date",
    key: "date",
    dataIndex: "date",
    width: "120px",
    sortable: true,
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {val ? new Date(val).toLocaleDateString() : "N/A"}
      </Text>
    ),
    filterConfig: {
      type: "date",
      matchFn: (record, val) =>
        !val ||
        new Date(record.date).toDateString() === new Date(val).toDateString(),
    },
  },
  {
    title: "Teacher Status",
    key: "isTeacherComplete",
    dataIndex: "isTeacherComplete",
    width: "145px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Observer Status",
    key: "isCoordinatorComplete",
    dataIndex: "isCoordinatorComplete",
    width: "150px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Teacher Score",
    key: "teacherScore",
    dataIndex: "teacherScore",
    width: "140px",
    sortable: true,
    sorter: (a, b) => (a.teacherScore || 0) - (b.teacherScore || 0),
    render: (val, record) => {
      const hasScore = record.teacherTotal > 0 && val !== undefined && val !== null;
      return (
        <Text
          fontSize="sm"
          fontWeight="600"
          color={hasScore ? "brand.primary" : "gray.400"}
        >
          {hasScore ? `${val} / ${record.teacherTotal}` : "—"}
        </Text>
      );
    },
  },
  {
    title: "Observer Score",
    key: "observerScore",
    dataIndex: "observerScore",
    width: "145px",
    sortable: true,
    sorter: (a, b) => (a.observerScore || 0) - (b.observerScore || 0),
    render: (val, record) => {
      const hasScore = record.observerTotal > 0 && val !== undefined && val !== null;
      return (
        <Text
          fontSize="sm"
          fontWeight="600"
          color={hasScore ? "brand.primary" : "gray.400"}
        >
          {hasScore ? `${val} / ${record.observerTotal}` : "—"}
        </Text>
      );
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "200px",
    render: (_, record) => (
      <Flex gap={1} align="center">
        <Link to={`/fortnightly-monitor/report/${record._id}`}>
          <Button
            size="md"
            variant="outline"
            colorScheme="blue"
            fontWeight="medium"
            flexShrink={0}
          >
            View Report
          </Button>
        </Link>
        {currentUserRole === UserRole[1] && onDelete && (
          <Button
            size="md"
            variant="outline"
            colorScheme="red"
            px={2}
            flexShrink={0}
            onClick={() => onDelete(record._id)}
            title="Delete"
          >
            <DeleteFilled />
          </Button>
        )}
      </Flex>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 9. REPORTS — Form 2 (Classroom Walkthrough Report)
// ─────────────────────────────────────────────────────────────────────────────
export const getReportForm2Columns = ({
  data = [],
  filterOptions = {},
  currentUserRole,
  onDelete,
}) => [
  {
    title: "Observer Name",
    key: "observerName",
    dataIndex: "createdBy",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.createdBy?.name || "").localeCompare(b?.createdBy?.name || ""),
    render: (val) => <UserCell name={val?.name || "N/A"} user={val} fontWeight="500" />,
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.createdBy?.name)),
      matchFn: (record, vals) => vals.includes(record?.createdBy?.name || ""),
    },
  },
  {
    title: "Teacher Name",
    key: "teacherName",
    dataIndex: "grenralDetails",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.grenralDetails?.NameoftheVisitingTeacher?.name || "").localeCompare(
        b?.grenralDetails?.NameoftheVisitingTeacher?.name || "",
      ),
    render: (val) => {
      const userObj = val?.NameoftheVisitingTeacher;
      return (
        <UserCell
          name={userObj?.name || (typeof userObj === "string" ? userObj : "N/A")}
          user={userObj}
        />
      );
    },
    filterConfig: {
      type: "select",
      options: uniq(
        data.map((r) => r?.grenralDetails?.NameoftheVisitingTeacher?.name),
      ),
      matchFn: (record, vals) =>
        vals.includes(
          record?.grenralDetails?.NameoftheVisitingTeacher?.name || "",
        ),
    },
  },
  {
    title: "Class",
    key: "className",
    dataIndex: "grenralDetails",
    width: "110px",
    sortable: true,
    sorter: (a, b) =>
      extractClassName(a?.grenralDetails?.className).localeCompare(
        extractClassName(b?.grenralDetails?.className),
      ),
    render: (val) => (
      <Text fontSize="sm">{extractClassName(val?.className)}</Text>
    ),
    filterConfig: {
      type: "select",
      options: filterOptions?.classes?.length
        ? filterOptions.classes
        : uniq(
            data.map((r) => extractClassName(r?.grenralDetails?.className)),
          ),
      matchFn: (record, vals) =>
        vals.includes(extractClassName(record?.grenralDetails?.className)),
    },
  },
  {
    title: "Section",
    key: "section",
    dataIndex: "grenralDetails",
    width: "90px",
    sortable: true,
    render: (val) => <Text fontSize="sm">{val?.Section || "N/A"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.sections?.length
        ? filterOptions.sections
        : uniq(data.map((r) => r?.grenralDetails?.Section)),
      matchFn: (record, vals) =>
        vals.includes(record?.grenralDetails?.Section || ""),
    },
  },
  {
    title: "Subject",
    key: "subject",
    dataIndex: "grenralDetails",
    width: "110px",
    sortable: true,
    render: (val) => <Text fontSize="sm">{val?.Subject || "N/A"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.subjects?.length
        ? filterOptions.subjects
        : uniq(data.map((r) => r?.grenralDetails?.Subject)),
      matchFn: (record, vals) =>
        vals.includes(record?.grenralDetails?.Subject || ""),
    },
  },
  {
    title: "Date",
    key: "observationDate",
    dataIndex: "grenralDetails",
    width: "130px",
    sortable: true,
    sorter: (a, b) =>
      new Date(a?.grenralDetails?.DateOfObservation) -
      new Date(b?.grenralDetails?.DateOfObservation),
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {val?.DateOfObservation
          ? new Date(val.DateOfObservation).toLocaleDateString()
          : "N/A"}
      </Text>
    ),
    filterConfig: {
      type: "date",
      matchFn: (record, val) =>
        !val ||
        new Date(record?.grenralDetails?.DateOfObservation).toDateString() ===
          new Date(val).toDateString(),
    },
  },
  {
    title: "Total Score",
    key: "totalScores",
    dataIndex: "totalScores",
    width: "120px",
    sortable: true,
    sorter: (a, b) => (a.totalScores || 0) - (b.totalScores || 0),
    render: (val, record) => (
      <Text
        fontSize="sm"
        fontWeight="600"
        color={val ? "brand.primary" : "gray.400"}
      >
        {val
          ? record?.scoreOutof > 0
            ? `${val} / ${record.scoreOutof}`
            : val
          : "N/A"}
      </Text>
    ),
  },
  {
    title: "Teacher Status",
    key: "isTeacherCompletes",
    dataIndex: "isTeacherCompletes",
    width: "145px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Observer Status",
    key: "isObserverCompleted",
    dataIndex: "isObserverCompleted",
    width: "150px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "200px",
    render: (_, record) => (
      <Flex gap={1} align="center">
        <Link to={`/classroom-walkthrough/report/${record._id}`}>
          <Button
            size="md"
            variant="outline"
            colorScheme="blue"
            fontWeight="medium"
            flexShrink={0}
          >
            View Report
          </Button>
        </Link>
        {currentUserRole === UserRole[1] && onDelete && (
          <Button
            size="md"
            variant="outline"
            colorScheme="red"
            px={2}
            flexShrink={0}
            onClick={() => onDelete(record._id)}
            title="Delete"
          >
            <DeleteFilled />
          </Button>
        )}
      </Flex>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 10. REPORTS — Form 3 (Notebook Checking Report)
// ─────────────────────────────────────────────────────────────────────────────
const calculateNotebookScore = (formName) => {
  let totalScore = 0,
    outOfScore = 0;
  const keys = [
    "maintenanceOfNotebooks",
    "qualityOfOppurtunities",
    "qualityOfTeacherFeedback",
    "qualityOfLearner",
  ];
  keys.forEach((section) => {
    if (formName?.[section]) {
      formName[section].forEach((item) => {
        const ans = item?.answer;
        if (["1", "2", "3"].includes(ans)) {
          totalScore += parseInt(ans, 10);
          outOfScore += 3;
        }
      });
    }
  });
  return { totalScore, outOfScore };
};

export const getReportForm3Columns = ({
  data = [],
  filterOptions = {},
  currentUserRole,
  onDelete,
}) => [
  {
    title: "Observer Name",
    key: "observerName",
    dataIndex: "grenralDetails",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (
        a?.grenralDetails?.NameofObserver?.name ||
        a?.createdBy?.name ||
        ""
      ).localeCompare(
        b?.grenralDetails?.NameofObserver?.name || b?.createdBy?.name || "",
      ),
    render: (val, record) => {
      const userObj = val?.NameofObserver || record?.createdBy;
      return (
        <UserCell
          name={userObj?.name || (typeof userObj === "string" ? userObj : "N/A")}
          user={userObj}
          fontWeight="500"
        />
      );
    },
    filterConfig: {
      type: "select",
      options: uniq(
        data.map(
          (r) => r?.grenralDetails?.NameofObserver?.name || r?.createdBy?.name,
        ),
      ),
      matchFn: (record, vals) =>
        vals.includes(
          record?.grenralDetails?.NameofObserver?.name ||
            record?.createdBy?.name ||
            "",
        ),
    },
  },
  {
    title: "Teacher Name",
    key: "teacherName",
    dataIndex: "teacherID",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.teacherID?.name || a?.createdBy?.name || "").localeCompare(
        b?.teacherID?.name || b?.createdBy?.name || "",
      ),
    render: (val, record) => {
      const userObj = val || record?.createdBy;
      return <UserCell name={userObj?.name || "N/A"} user={userObj} />;
    },
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.teacherID?.name || r?.createdBy?.name)),
      matchFn: (record, vals) =>
        vals.includes(record?.teacherID?.name || record?.createdBy?.name || ""),
    },
  },
  {
    title: "Class",
    key: "className",
    dataIndex: "grenralDetails",
    width: "110px",
    sortable: true,
    sorter: (a, b) =>
      extractClassName(a?.grenralDetails?.className).localeCompare(
        extractClassName(b?.grenralDetails?.className),
      ),
    render: (val) => (
      <Text fontSize="sm">{extractClassName(val?.className)}</Text>
    ),
    filterConfig: {
      type: "select",
      options: filterOptions?.classes?.length
        ? filterOptions.classes
        : uniq(
            data.map((r) => extractClassName(r?.grenralDetails?.className)),
          ),
      matchFn: (record, vals) =>
        vals.includes(extractClassName(record?.grenralDetails?.className)),
    },
  },
  {
    title: "Section",
    key: "section",
    dataIndex: "grenralDetails",
    width: "90px",
    sortable: true,
    render: (val) => <Text fontSize="sm">{val?.Section || "N/A"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.sections?.length
        ? filterOptions.sections
        : uniq(data.map((r) => r?.grenralDetails?.Section)),
      matchFn: (record, vals) =>
        vals.includes(record?.grenralDetails?.Section || ""),
    },
  },
  {
    title: "Subject",
    key: "subject",
    dataIndex: "grenralDetails",
    width: "110px",
    sortable: true,
    render: (val) => <Text fontSize="sm">{val?.Subject || "N/A"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.subjects?.length
        ? filterOptions.subjects
        : uniq(data.map((r) => r?.grenralDetails?.Subject)),
      matchFn: (record, vals) =>
        vals.includes(record?.grenralDetails?.Subject || ""),
    },
  },
  {
    title: "Observation Date",
    key: "observationDate",
    dataIndex: "grenralDetails",
    width: "140px",
    sortable: true,
    sorter: (a, b) =>
      new Date(a?.grenralDetails?.DateOfObservation) -
      new Date(b?.grenralDetails?.DateOfObservation),
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {val?.DateOfObservation
          ? new Date(val.DateOfObservation).toLocaleDateString()
          : "N/A"}
      </Text>
    ),
    filterConfig: {
      type: "date",
      matchFn: (record, val) =>
        !val ||
        new Date(record?.grenralDetails?.DateOfObservation).toDateString() ===
          new Date(val).toDateString(),
    },
  },

  {
    title: "Teacher Status",
    key: "isTeacherComplete",
    dataIndex: "isTeacherComplete",
    width: "145px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Observer Status",
    key: "isObserverComplete",
    dataIndex: "isObserverComplete",
    width: "150px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Reflection Status",
    key: "isReflation",
    dataIndex: "isReflation",
    width: "155px",
    render: (val) => <StatusBadge value={val} trueLabel="Submitted" falseLabel="Pending" />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Submitted",
      falseLabel: "Pending",
    },
  },

  {
    title: "Teacher Score",
    key: "teacherScore",
    width: "140px",
    render: (_, record) => {
      const score = calculateNotebookScore(record?.TeacherForm);
      return score.outOfScore > 0 ? (
        <Text fontSize="sm" fontWeight="600" color="brand.primary">
          {score.totalScore} / {score.outOfScore}
        </Text>
      ) : (
        <Text fontSize="sm" color="gray.400">
          N/A
        </Text>
      );
    },
  },
  {
    title: "Observer Score",
    key: "observerScore",
    width: "145px",
    render: (_, record) => {
      const score = calculateNotebookScore(record?.ObserverForm);
      return score.outOfScore > 0 ? (
        <Text fontSize="sm" fontWeight="600" color="brand.primary">
          {score.totalScore} / {score.outOfScore}
        </Text>
      ) : (
        <Text fontSize="sm" color="gray.400">
          N/A
        </Text>
      );
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "280px",
    render: (_, record) => (
      <Flex gap={1} align="center">
        <Link to={`/notebook-checking-proforma/report/${record._id}`}>
          <Button
            size="md"
            variant="outline"
            colorScheme="blue"
            fontWeight="medium"
            flexShrink={0}
          >
            View Report
          </Button>
        </Link>
        {currentUserRole === UserRole[2] && record.isTeacherComplete && record.isObserverComplete && !record.isReflation && (
          <Link to={`/notebook-checking-proforma/complete/${record._id}`}>
            <Button
              size="md"
              variant="outline"
              colorScheme="orange"
              fontWeight="medium"
              flexShrink={0}
            >
              Reflection
            </Button>
          </Link>
        )}
        {currentUserRole === UserRole[1] && onDelete && (
          <Button
            size="md"
            variant="outline"
            colorScheme="red"
            px={2}
            flexShrink={0}
            onClick={() => onDelete(record._id)}
            title="Delete"
          >
            <DeleteFilled />
          </Button>
        )}
      </Flex>
    ),
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// 11. REPORTS — Form 4 (Weekly 4 Form Report)
// ─────────────────────────────────────────────────────────────────────────────
export const getReportForm4Columns = ({ data = [], currentUserRole, onDelete }) => [
  {
    title: "Observer Name",
    key: "observerName",
    dataIndex: "isInitiated",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.isInitiated?.Observer?.name || "").localeCompare(
        b?.isInitiated?.Observer?.name || "",
      ),
    render: (val) => (
      <UserCell
        name={val?.Observer?.name || "N/A"}
        user={val?.Observer}
        fontWeight="500"
      />
    ),
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.isInitiated?.Observer?.name)),
      matchFn: (record, vals) =>
        vals.includes(record?.isInitiated?.Observer?.name || ""),
    },
  },
  {
    title: "Teacher Name",
    key: "teacherName",
    dataIndex: "teacherId",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.teacherId?.name || "").localeCompare(b?.teacherId?.name || ""),
    render: (val) => <UserCell name={val?.name || "N/A"} user={val} />,
    filterConfig: {
      type: "select",
      options: uniq(data.map((r) => r?.teacherId?.name)),
      matchFn: (record, vals) => vals.includes(record?.teacherId?.name || ""),
    },
  },
  {
    title: "Date of Submission",
    key: "dateOfSubmission",
    dataIndex: "dateOfSubmission",
    width: "155px",
    sortable: true,
    sorter: (a, b) =>
      new Date(a.dateOfSubmission) - new Date(b.dateOfSubmission),
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {val ? new Date(val).toLocaleDateString() : "N/A"}
      </Text>
    ),
    filterConfig: {
      type: "date",
      matchFn: (record, val) =>
        !val ||
        new Date(record.dateOfSubmission).toDateString() ===
          new Date(val).toDateString(),
    },
  },
  {
    title: "Status",
    key: "isCompleted",
    dataIndex: "isCompleted",
    width: "130px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "200px",
    render: (_, record) => (
      <Flex gap={2} align="center">
        <Link to={`/weekly4form/report/${record._id}`}>
          <Button
            size="md"
            variant="outline"
            colorScheme="blue"
            fontWeight="medium"
            flexShrink={0}
          >
            View Report
          </Button>
        </Link>
        {(currentUserRole === "Superadmin" || currentUserRole === "Observer") && onDelete && (
          <Button
            size="md"
            variant="outline"
            colorScheme="red"
            px={2}
            flexShrink={0}
            onClick={() => onDelete(record._id)}
            title="Delete"
          >
            <DeleteFilled />
          </Button>
        )}
      </Flex>
    ),
  },
];


export const getCoScholasticColumns = ({
  data = [],
  filterOptions = {},
  currentUserRole,
  onDelete,
}) => [
  {
    title: "Observer Name",
    key: "observerName",
    dataIndex: "createdBy",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) =>
      (a?.createdBy?.name || a?.userId?.name || "").localeCompare(
        b?.createdBy?.name || b?.userId?.name || "",
      ),
    render: (val, record) => {
      const userObj = val || record?.userId;
      return <UserCell name={userObj?.name || "—"} user={userObj} fontWeight="500" />;
    },
    filterConfig: {
      type: "select",
      options: [...new Set(data.map((r) => r?.createdBy?.name || r?.userId?.name).filter(Boolean))],
      matchFn: (record, vals) =>
        vals.includes(record?.createdBy?.name || record?.userId?.name || ""),
    },
  },
  {
    title: "Teacher Name",
    key: "teacherName",
    dataIndex: "grenralDetails",
    width: "250px",
    minWidth: "220px",
    sortable: true,
    sorter: (a, b) => {
      const aName = a?.grenralDetails?.NameoftheVisitingTeacher?.name || a?.teacherID?.name || (typeof a?.grenralDetails?.NameoftheVisitingTeacher === "string" ? a?.grenralDetails?.NameoftheVisitingTeacher : "");
      const bName = b?.grenralDetails?.NameoftheVisitingTeacher?.name || b?.teacherID?.name || (typeof b?.grenralDetails?.NameoftheVisitingTeacher === "string" ? b?.grenralDetails?.NameoftheVisitingTeacher : "");
      return aName.localeCompare(bName);
    },
    render: (val, record) => {
      const userObj = val?.NameoftheVisitingTeacher || record?.teacherID;
      return (
        <UserCell
          name={userObj?.name || (typeof userObj === "string" ? userObj : "—")}
          user={userObj}
          color="gray.700"
        />
      );
    },
    filterConfig: {
      type: "select",
      options: [...new Set(data.map((r) => r?.grenralDetails?.NameoftheVisitingTeacher?.name || r?.teacherID?.name || (typeof r?.grenralDetails?.NameoftheVisitingTeacher === "string" ? r?.grenralDetails?.NameoftheVisitingTeacher : null)).filter(Boolean))],
      matchFn: (record, vals) => {
        const name = record?.grenralDetails?.NameoftheVisitingTeacher?.name || record?.teacherID?.name || (typeof record?.grenralDetails?.NameoftheVisitingTeacher === "string" ? record?.grenralDetails?.NameoftheVisitingTeacher : "");
        return vals.includes(name || "");
      },
    },
  },
  {
    title: "Class",
    key: "className",
    dataIndex: "grenralDetails",
    width: "110px",
    sortable: true,
    sorter: (a, b) =>
      extractClassName(a?.grenralDetails?.className).localeCompare(
        extractClassName(b?.grenralDetails?.className),
      ),
    render: (val) => (
      <Text fontSize="sm">{extractClassName(val?.className)}</Text>
    ),
    filterConfig: {
      type: "select",
      options: filterOptions?.classes?.length
        ? filterOptions.classes
        : uniq(
            data.map((r) => extractClassName(r?.grenralDetails?.className)),
          ),
      matchFn: (record, vals) =>
        vals.includes(extractClassName(record?.grenralDetails?.className)),
    },
  },
  {
    title: "Section",
    key: "section",
    dataIndex: "grenralDetails",
    width: "90px",
    sortable: true,
    sorter: (a, b) =>
      (a?.grenralDetails?.Section || "").localeCompare(
        b?.grenralDetails?.Section || "",
      ),
    render: (val) => <Text fontSize="sm">{val?.Section || "—"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.sections?.length
        ? filterOptions.sections
        : [...new Set(data.map((r) => r?.grenralDetails?.Section).filter(Boolean))],
      matchFn: (record, vals) =>
        vals.includes(record?.grenralDetails?.Section || ""),
    },
  },
  {
    title: "Subject",
    key: "subject",
    dataIndex: "grenralDetails",
    width: "110px",
    sortable: true,
    sorter: (a, b) =>
      (a?.grenralDetails?.Subject || "").localeCompare(
        b?.grenralDetails?.Subject || "",
      ),
    render: (val) => <Text fontSize="sm">{val?.Subject || "—"}</Text>,
    filterConfig: {
      type: "select",
      options: filterOptions?.subjects?.length
        ? filterOptions.subjects
        : [...new Set(data.map((r) => r?.grenralDetails?.Subject).filter(Boolean))],
      matchFn: (record, vals) =>
        vals.includes(record?.grenralDetails?.Subject || ""),
    },
  },
  {
    title: "Observation Date",
    key: "observationDate",
    dataIndex: "grenralDetails",
    width: "140px",
    sortable: true,
    sorter: (a, b) =>
      new Date(a?.grenralDetails?.DateOfObservation) -
      new Date(b?.grenralDetails?.DateOfObservation),
    render: (val) => (
      <Text fontSize="sm" color="gray.600">
        {val?.DateOfObservation ? new Date(val.DateOfObservation).toLocaleDateString() : "—"}
      </Text>
    ),
    filterConfig: {
      type: "date",
      matchFn: (record, val) =>
        !val ||
        new Date(record?.grenralDetails?.DateOfObservation).toDateString() ===
          new Date(val).toDateString(),
    },
  },
  {
    title: "Teacher Status",
    key: "isTeacherCompletes",
    dataIndex: "isTeacherCompletes",
    width: "140px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Observer Status",
    key: "isObserverCompleted",
    dataIndex: "isObserverCompleted",
    width: "145px",
    render: (val) => <StatusBadge value={val} />,
    filterConfig: {
      type: "boolean",
      trueLabel: "Completed",
      falseLabel: "Pending",
    },
  },
  {
    title: "Action",
    key: "action",
    dataIndex: "action",
    width: "320px",
    render: (_, record) => {
      const { isTeacherCompletes, isObserverCompleted } = record;
      const renderAction = () => {
        return (
          <>
            <Link to={`/co-scholastic/report/${record._id}`}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                fontWeight="medium"
                flexShrink={0}
              >
                View Report
              </Button>
            </Link>

            {((currentUserRole === "Observer" && !isObserverCompleted) ||
              (currentUserRole === "Teacher" && !isTeacherCompletes)) && (
              <Link to={`/co-scholastic/create/${record._id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  Continue Form
                </Button>
              </Link>
            )}

            {(currentUserRole === "Superadmin" || currentUserRole === "Observer") && (
              <Link to={`/co-scholastic/edit/${record._id}`}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="green"
                  fontWeight="medium"
                  flexShrink={0}
                >
                  Edit
                </Button>
              </Link>
            )}

            {(!isTeacherCompletes || !isObserverCompleted) && (currentUserRole === "Superadmin" || (currentUserRole === "Observer" && isObserverCompleted)) && (
              <Reminder id={record?._id} type="coscholastic" size="sm" />
            )}
          </>
        );
      };

      return (
        <Flex gap={2} align="center" flexWrap="nowrap">
          {renderAction()}
          {(currentUserRole === "Superadmin" || currentUserRole === "Observer") && onDelete && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              px={2.5}
              flexShrink={0}
              onClick={() => onDelete(record._id)}
              title="Delete"
            >
              <DeleteFilled />
            </Button>
          )}
        </Flex>
      );
    },
  },
];

